import * as React from 'react'

import { formatPlatformName } from '@/components/dashboard/PlatformIcon'

// ─── Types ──────────────────────────────────────────────────────────────────

export type OnboardingStep =
  | 'greeting'
  | 'asking_website'
  | 'scanning'
  | 'confirming_context'
  | 'asking_context_manual'
  | 'suggesting_prompts'
  | 'choosing_platform'
  | 'running_demo'
  | 'reviewing_response'
  | 'confirming_competitors'
  | 'finishing'
  | 'done'

export type ScanStatus = 'running' | 'ready' | 'failed' | 'skipped'

export interface ScanResult {
  status: ScanStatus
  industry: string | null
  niche_description: string | null
  competitors: string[]
  suggested_brand_name: string | null
  confidence: 'high' | 'low' | null
  failure_reason: string | null
}

export interface DemoResult {
  platform: string
  brand_mentioned: boolean
  sentiment: 'positive' | 'neutral' | 'negative' | null
  mention_position: number | null
  response_text: string
  competitor_mentions: string[]
  brand_name: string
}

export interface OnboardingAnswers {
  websiteUrl?: string | undefined
  host?: string | undefined
  industry?: string | undefined
  nicheDescription?: string | undefined
  confidence?: 'high' | 'low' | null | undefined
  acceptedSuggestion?: boolean | undefined
  suggestedPrompts?: string[] | undefined
  usedAi?: boolean | undefined
  demoPrompt?: string | undefined
  demoPlatform?: string | undefined
  demoResult?: DemoResult | undefined
  demoFailed?: boolean | undefined
  demoRetried?: boolean | undefined
  // Set via MARK_DEMO_DISPATCHED, synchronously before the ticket-2 caller
  // fires the /api/simulate mutation — see the comment on the 'running_demo'
  // transition below for why this ordering is a hard requirement, not a
  // style preference.
  demoDispatchedAt?: number | undefined
  confirmedCompetitors?: string[] | undefined
  clientName?: string | undefined
  trackingOwnSite?: boolean | undefined
}

export type PromptsErrorKind = 'insufficient_credits' | 'generic'

export type TurnComponent =
  | 'website'
  | 'context'
  | 'context_manual'
  | 'prompts'
  | 'platform_picker'
  | 'demo_response'
  | 'competitor_confirm'

export interface Turn {
  id: string
  role: 'assistant' | 'user'
  kind: 'text' | 'status' | 'interactive'
  text?: string
  component?: TurnComponent
  resolved?: boolean
}

export interface OnboardingMachineState {
  firstName: string
  // Persisted on the machine (not re-derived per dispatch) so RESTORE can
  // synchronously rebuild the same transcript variant the user originally
  // saw, without depending on a fresh status refetch resolving first.
  canCreateClients: boolean
  step: OnboardingStep
  websiteId: string | null
  answers: OnboardingAnswers
  turns: Turn[]
  error: string | null
  promptsError: PromptsErrorKind | null
}

export type OnboardingAction =
  | { type: 'RESTORE'; step: OnboardingStep; websiteId: string | null; answers: OnboardingAnswers }
  | { type: 'SUBMIT_WEBSITE'; url: string; clientName?: string | undefined }
  | { type: 'WEBSITE_SAVED'; websiteId: string; scanDispatched: boolean }
  | { type: 'WEBSITE_FAILED'; message: string }
  | { type: 'SCAN_UPDATE'; scan: ScanResult }
  | { type: 'SCAN_TIMEOUT' }
  | { type: 'SUBMIT_CONTEXT'; industry: string; nicheDescription: string; acceptedSuggestion: boolean }
  | { type: 'PROMPTS_LOADED'; prompts: string[]; usedAi: boolean }
  | { type: 'PROMPTS_ERROR'; kind: PromptsErrorKind }
  | { type: 'SELECT_DEMO_PROMPT'; prompt: string }
  | { type: 'SKIP_PROMPTS' }
  | { type: 'SELECT_DEMO_PLATFORM'; platform: string }
  // Must be dispatched SYNCHRONOUSLY by the caller (ticket 2's
  // OnboardingChat.tsx) before it calls mutate() on the /api/simulate
  // request — this is what lets RESTORE tell "a demo request may already
  // have spent a credit" apart from "the user never got past picking a
  // platform". See the RESTORE handler for the fail-closed policy this
  // exists to support.
  | { type: 'MARK_DEMO_DISPATCHED' }
  | { type: 'DEMO_RESULT'; result: DemoResult }
  | { type: 'DEMO_FAILED'; retried: boolean }
  | { type: 'CONTINUE_TO_COMPETITORS' }
  | { type: 'CONFIRM_COMPETITORS'; names: string[] }
  | { type: 'FINISHED' }

// ─── Copy ───────────────────────────────────────────────────────────────────

export const ASKING_WEBSITE_TEXT =
  "First up — what's your website? I'll take a look and figure out the rest myself."

export const ASKING_WEBSITE_MANAGED_TEXT =
  "Let's set up the first brand you want to track. Whose site is it — your own, or a client's you're managing?"

export const ASKING_WEBSITE_RETRY_TEXT = "That didn't go through — mind trying again?"

export function scanningText(host: string): string {
  return `Got it — scanning **${host}** now, this takes about 15 seconds…`
}

export function confirmingContextText(industry: string, niche: string): string {
  // Scan-derived niche descriptions often already end in a period — strip
  // trailing ones so the template's own "." doesn't produce "..".
  const trimmedNiche = niche.replace(/\.+\s*$/, '')
  return `Looks like you're in the **${industry}** space${trimmedNiche ? ` — ${trimmedNiche}` : ''}. Sound right?`
}

export const CONFIRMING_CONTEXT_LOW_CONFIDENCE_TEXT =
  "I had a look but I'm not certain — which of these fits best?"

export const ASKING_CONTEXT_MANUAL_TEXT =
  "I couldn't get a read on the site — no problem, I'll just ask. What industry are you in?"

export const SUGGESTING_PROMPTS_TEXT =
  "Based on what's on your site, here's what I think your customers are asking AI assistants. Pick one to preview live."

export function choosingPlatformText(prompt: string): string {
  return `Want to see a live example? Pick a platform and I'll run **"${prompt}"** right now.`
}

export function runningDemoText(platform: string): string {
  return `Running that prompt on **${formatPlatformName(platform)}** now…`
}

export const DEMO_FAILED_TEXT = "That didn't go through, but no worries — your suggestions are waiting on the Prompts page whenever you're ready to track and run them."

export const DEMO_FAILED_RETRY_TEXT = "That didn't go through — want to try a different platform, or continue without it?"

// Shown in place of the demo_response interactive turn when we've diverted
// here from RESTORE rather than a real completed run — see the RESTORE
// handler's credit-double-spend guard. Distinguish the two cases by checking
// answers.demoResult === undefined at reviewing_response.
export const DEMO_INTERRUPTED_TEXT = 'Looks like that run got cut short. Want to try again?'

export const NO_COMPETITORS_TEXT = "Didn't spot any other brands in that response — you can always add competitors to track later."

// ─── Helpers ────────────────────────────────────────────────────────────────

export function hostFromUrl(url: string): string {
  try {
    const withProto = /^https?:\/\//i.test(url) ? url : `https://${url}`
    return new URL(withProto).hostname
  } catch {
    return url
  }
}

let turnCounter = 0
function nextTurnId(): string {
  turnCounter += 1
  return `t${turnCounter}`
}

function assistantText(text: string): Turn {
  return { id: nextTurnId(), role: 'assistant', kind: 'text', text }
}

function assistantInteractive(text: string, component: TurnComponent): Turn {
  return { id: nextTurnId(), role: 'assistant', kind: 'interactive', text, component, resolved: false }
}

function assistantStatus(text: string): Turn {
  return { id: nextTurnId(), role: 'assistant', kind: 'status', text }
}

function userTurn(text: string): Turn {
  return { id: nextTurnId(), role: 'user', kind: 'text', text }
}

function resolveLastInteractive(turns: Turn[]): Turn[] {
  return turns.map((t, i) => (i === turns.length - 1 && t.kind === 'interactive' ? { ...t, resolved: true } : t))
}

// ─── Initial transcript reconstruction (used on restore, never persisted) ──

export function buildGreetingTurns(firstName: string, canCreateClients: boolean): Turn[] {
  turnCounter = 0
  const name = firstName !== 'there' ? `, ${firstName}` : ''
  return [
    assistantText(`Welcome to Lensora${name}! Let's get you set up — takes about a minute.`),
    assistantInteractive(canCreateClients ? ASKING_WEBSITE_MANAGED_TEXT : ASKING_WEBSITE_TEXT, 'website'),
  ]
}

const STEP_ORDER: OnboardingStep[] = [
  'asking_website', 'scanning', 'confirming_context', 'asking_context_manual',
  'suggesting_prompts', 'choosing_platform', 'running_demo', 'reviewing_response',
  'confirming_competitors', 'finishing', 'done',
]

function isAtOrPast(step: OnboardingStep, target: OnboardingStep): boolean {
  return STEP_ORDER.indexOf(step) >= STEP_ORDER.indexOf(target)
}

// Rebuilds the full transcript from persisted {step, answers} alone — the
// rendered transcript itself is never persisted, so returning users never
// see stale bubble copy from a previous app version.
export function rebuildTranscript(
  firstName: string,
  canCreateClients: boolean,
  step: OnboardingStep,
  answers: OnboardingAnswers
): Turn[] {
  const turns = buildGreetingTurns(firstName, canCreateClients)
  if (step === 'asking_website') return turns

  const host = answers.host ?? answers.websiteUrl ?? ''
  const hostEcho = answers.clientName ? `${host} (for ${answers.clientName})` : host
  turns[turns.length - 1] = { ...turns[turns.length - 1]!, resolved: true }
  turns.push(userTurn(hostEcho))
  turns.push(assistantStatus(scanningText(host)))
  if (step === 'scanning') return turns

  const isManual = step === 'asking_context_manual' && answers.confidence === undefined
  const contextComponent: TurnComponent = isManual ? 'context_manual' : 'context'
  const contextText =
    contextComponent === 'context_manual'
      ? ASKING_CONTEXT_MANUAL_TEXT
      : answers.confidence === 'high' && answers.industry
        ? confirmingContextText(answers.industry, answers.nicheDescription ?? '')
        : CONFIRMING_CONTEXT_LOW_CONFIDENCE_TEXT
  turns.push(assistantInteractive(contextText, contextComponent))
  if (!isAtOrPast(step, 'suggesting_prompts')) return turns

  turns[turns.length - 1] = { ...turns[turns.length - 1]!, resolved: true }
  const echo = answers.acceptedSuggestion
    ? `Yes, ${answers.industry}`
    : `${answers.industry ?? ''}${answers.nicheDescription ? ` — ${answers.nicheDescription}` : ''}`
  turns.push(userTurn(echo))
  turns.push(assistantStatus(SUGGESTING_PROMPTS_TEXT))
  if (answers.suggestedPrompts !== undefined) {
    turns.push(assistantInteractive(SUGGESTING_PROMPTS_TEXT, 'prompts'))
  }
  if (!isAtOrPast(step, 'choosing_platform')) return turns

  if (turns[turns.length - 1]?.kind === 'interactive') {
    turns[turns.length - 1] = { ...turns[turns.length - 1]!, resolved: true }
  }
  // No prompt selected (SKIP_PROMPTS) — nothing further to rebuild, mirrors
  // the reducer skipping straight to 'finishing' in that case.
  if (!answers.demoPrompt) {
    turns.push(userTurn("No prompts for now — I'll add them later."))
    return turns
  }
  turns.push(userTurn(`Test: "${answers.demoPrompt}"`))
  turns.push(assistantInteractive(choosingPlatformText(answers.demoPrompt), 'platform_picker'))
  if (!isAtOrPast(step, 'running_demo')) return turns

  if (turns[turns.length - 1]?.kind === 'interactive') {
    turns[turns.length - 1] = { ...turns[turns.length - 1]!, resolved: true }
  }
  if (answers.demoPlatform) {
    turns.push(userTurn(formatPlatformName(answers.demoPlatform)))
    turns.push(assistantStatus(runningDemoText(answers.demoPlatform)))
  }
  if (!isAtOrPast(step, 'reviewing_response')) return turns

  // answers.demoResult === undefined here means RESTORE diverted a
  // mid-flight 'running_demo' straight to 'reviewing_response' (the
  // credit-double-spend guard) rather than this being a genuinely completed
  // run — render the interrupted copy instead of a dead interactive turn.
  if (answers.demoResult !== undefined) {
    turns.push(assistantInteractive('Here\'s what came back:', 'demo_response'))
  } else {
    turns.push(assistantText(DEMO_INTERRUPTED_TEXT))
  }
  if (!isAtOrPast(step, 'confirming_competitors')) return turns

  const hasCompetitorMentions = (answers.demoResult?.competitor_mentions.length ?? 0) > 0
  if (answers.demoResult !== undefined && hasCompetitorMentions) {
    if (turns[turns.length - 1]?.kind === 'interactive') {
      turns[turns.length - 1] = { ...turns[turns.length - 1]!, resolved: true }
    }
    turns.push(assistantInteractive('Which of these are actually your competitors?', 'competitor_confirm'))
  }
  if (!isAtOrPast(step, 'finishing')) return turns

  if (hasCompetitorMentions && turns[turns.length - 1]?.component === 'competitor_confirm') {
    turns[turns.length - 1] = { ...turns[turns.length - 1]!, resolved: true }
  }
  if (answers.demoFailed) {
    turns.push(assistantText(DEMO_FAILED_TEXT))
  }
  return turns
}

// ─── Reducer ────────────────────────────────────────────────────────────────

export function onboardingReducer(state: OnboardingMachineState, action: OnboardingAction): OnboardingMachineState {
  switch (action.type) {
    case 'RESTORE': {
      // Hard requirement: 'scanning' and anything past it requires a real
      // websiteId. 'scanning' specifically needs one for useScanPolling to
      // ever start polling — a restore into 'scanning' with a null
      // websiteId (which can happen if a website submission failed after
      // the optimistic step-change had already been persisted, on an older
      // client, or any other corrupt-storage edge case) silently never
      // polls anything and never times out, stranding the user on a dead
      // "scanning" screen with no recovery path. Falling back to
      // asking_website — same as the suggesting_prompts+ case below —
      // means a bad persisted state always self-heals on next load rather
      // than requiring the user to clear storage manually.
      if (action.websiteId === null && isAtOrPast(action.step, 'scanning')) {
        return {
          ...state,
          step: 'asking_website',
          websiteId: null,
          answers: {},
          turns: rebuildTranscript(state.firstName, state.canCreateClients, 'asking_website', {}),
          promptsError: null,
          error: null,
        }
      }
      // Security-mandated, fail-closed credit guard: a restore landing on
      // 'running_demo' can never be trusted to know whether the
      // /api/simulate request that would fire on remount already spent a
      // credit — demoDispatchedAt being set only proves the request was
      // SENT, not whether it resolved before the reload. Divert
      // unconditionally to 'reviewing_response' with no demoResult (the
      // "interrupted" case) rather than ever re-entering 'running_demo',
      // regardless of demoDispatchedAt's value.
      const restoreStep = action.step === 'running_demo' ? 'reviewing_response' : action.step
      const restoreAnswers = action.step === 'running_demo'
        ? { ...action.answers, demoResult: undefined, demoDispatchedAt: undefined }
        : action.answers
      return {
        ...state,
        step: restoreStep,
        websiteId: action.websiteId,
        answers: restoreAnswers,
        turns: rebuildTranscript(state.firstName, state.canCreateClients, restoreStep, restoreAnswers),
        promptsError: null,
        error: null,
      }
    }

    case 'SUBMIT_WEBSITE': {
      const host = hostFromUrl(action.url)
      const hostEcho = action.clientName ? `${host} (for ${action.clientName})` : host
      const turns = resolveLastInteractive(state.turns)
      turns.push(userTurn(hostEcho))
      return {
        ...state,
        step: 'scanning',
        error: null,
        answers: { ...state.answers, websiteUrl: action.url, host, clientName: action.clientName },
        turns,
      }
    }

    case 'WEBSITE_FAILED': {
      // SUBMIT_WEBSITE optimistically moves to 'scanning' before the request
      // resolves (so the UI reacts immediately), which means a failure here
      // must actively revert step/websiteId rather than just recording the
      // error — otherwise the optimistic 'scanning' state (with no real
      // websiteId) is what gets persisted to localStorage, and a reload
      // restores into a dead scanning screen that never polls anything
      // (useScanPolling needs a non-null websiteId to start) and never times
      // out. Pushing a fresh unresolved 'website' turn re-opens the input,
      // same pattern as PROMPTS_ERROR re-opening the prompts turn.
      const turns = [...state.turns, assistantInteractive(ASKING_WEBSITE_RETRY_TEXT, 'website')]
      return { ...state, step: 'asking_website', websiteId: null, error: action.message, turns }
    }

    case 'WEBSITE_SAVED': {
      const turns = [...state.turns, assistantStatus(scanningText(state.answers.host ?? state.answers.websiteUrl ?? ''))]
      if (!action.scanDispatched) {
        // No scan to wait for — go straight to the manual fallback.
        turns.push(assistantInteractive(ASKING_CONTEXT_MANUAL_TEXT, 'context_manual'))
        return { ...state, step: 'asking_context_manual', websiteId: action.websiteId, turns }
      }
      return { ...state, step: 'scanning', websiteId: action.websiteId, turns }
    }

    case 'SCAN_UPDATE': {
      const scan = action.scan
      if (scan.status === 'running') return state
      if (scan.status === 'failed' || scan.status === 'skipped') {
        const turns = [...state.turns, assistantInteractive(ASKING_CONTEXT_MANUAL_TEXT, 'context_manual')]
        return { ...state, step: 'asking_context_manual', turns }
      }
      // ready
      const industry = scan.industry ?? ''
      const niche = scan.niche_description ?? ''
      const confidence = scan.confidence
      const text =
        confidence === 'high' && industry
          ? confirmingContextText(industry, niche)
          : CONFIRMING_CONTEXT_LOW_CONFIDENCE_TEXT
      const turns = [...state.turns, assistantInteractive(text, 'context')]
      return {
        ...state,
        step: 'confirming_context',
        turns,
        answers: {
          ...state.answers,
          industry: confidence === 'high' ? industry || undefined : undefined,
          nicheDescription: confidence === 'high' ? niche || undefined : undefined,
          confidence,
        },
      }
    }

    case 'SCAN_TIMEOUT': {
      const turns = [...state.turns, assistantInteractive(ASKING_CONTEXT_MANUAL_TEXT, 'context_manual')]
      return { ...state, step: 'asking_context_manual', turns }
    }

    // Hard requirement: this is the ONLY action that advances past
    // confirming_context / asking_context_manual, and it is only ever
    // dispatched from a user-triggered submit handler in ContextTurn — never
    // automatically from a scan/poll result. Do not add another transition
    // out of these two states.
    case 'SUBMIT_CONTEXT': {
      const echo = action.acceptedSuggestion
        ? `Yes, ${action.industry}`
        : `${action.industry}${action.nicheDescription ? ` — ${action.nicheDescription}` : ''}`
      const turns = resolveLastInteractive(state.turns)
      turns.push(userTurn(echo))
      turns.push(assistantStatus(SUGGESTING_PROMPTS_TEXT))
      return {
        ...state,
        step: 'suggesting_prompts',
        error: null,
        promptsError: null,
        turns,
        answers: {
          ...state.answers,
          industry: action.industry,
          nicheDescription: action.nicheDescription || undefined,
          acceptedSuggestion: action.acceptedSuggestion,
        },
      }
    }

    case 'PROMPTS_LOADED': {
      const turns = [...state.turns, assistantInteractive(SUGGESTING_PROMPTS_TEXT, 'prompts')]
      return {
        ...state,
        promptsError: null,
        turns,
        answers: { ...state.answers, suggestedPrompts: action.prompts, usedAi: action.usedAi },
      }
    }

    case 'PROMPTS_ERROR': {
      const turns = [...state.turns, assistantInteractive(SUGGESTING_PROMPTS_TEXT, 'prompts')]
      return { ...state, promptsError: action.kind, turns, answers: { ...state.answers, suggestedPrompts: [] } }
    }

    // Single-select only — features one prompt as a live example rather
    // than batch-running everything suggested. The rest stay available on
    // the Prompts page via the already-persisted suggestedPrompts.
    case 'SELECT_DEMO_PROMPT': {
      const turns = resolveLastInteractive(state.turns)
      turns.push(userTurn(`Test: "${action.prompt}"`))
      turns.push(assistantInteractive(choosingPlatformText(action.prompt), 'platform_picker'))
      return { ...state, step: 'choosing_platform', turns, answers: { ...state.answers, demoPrompt: action.prompt } }
    }

    case 'SKIP_PROMPTS': {
      const turns = resolveLastInteractive(state.turns)
      turns.push(userTurn("No prompts for now — I'll add them later."))
      return { ...state, step: 'finishing', turns, answers: state.answers }
    }

    // Only action that advances past choosing_platform, same discipline as
    // SUBMIT_CONTEXT for confirming_context — never auto-triggered.
    case 'SELECT_DEMO_PLATFORM': {
      const turns = resolveLastInteractive(state.turns)
      turns.push(userTurn(formatPlatformName(action.platform)))
      turns.push(assistantStatus(runningDemoText(action.platform)))
      return { ...state, step: 'running_demo', turns, answers: { ...state.answers, demoPlatform: action.platform } }
    }

    // Recorded so it lands in localStorage BEFORE the caller's fetch
    // resolves — see the OnboardingAction type comment and the RESTORE
    // handler's credit-guard comment for why this ordering matters.
    case 'MARK_DEMO_DISPATCHED': {
      return { ...state, answers: { ...state.answers, demoDispatchedAt: Date.now() } }
    }

    case 'DEMO_RESULT': {
      const turns = [...state.turns, assistantInteractive('Here\'s what came back:', 'demo_response')]
      return { ...state, step: 'reviewing_response', turns, answers: { ...state.answers, demoResult: action.result } }
    }

    case 'DEMO_FAILED': {
      if (!action.retried) {
        const turns = [
          ...state.turns,
          assistantText(DEMO_FAILED_RETRY_TEXT),
          assistantInteractive(choosingPlatformText(state.answers.demoPrompt ?? ''), 'platform_picker'),
        ]
        return { ...state, step: 'choosing_platform', turns, answers: { ...state.answers, demoRetried: true } }
      }
      const turns = [...state.turns, assistantText(DEMO_FAILED_TEXT)]
      return { ...state, step: 'finishing', turns, answers: { ...state.answers, demoFailed: true } }
    }

    // Skips confirming_competitors entirely when the one demo response
    // surfaced zero competitor names — nothing to confirm.
    case 'CONTINUE_TO_COMPETITORS': {
      const turns = resolveLastInteractive(state.turns)
      const mentions = state.answers.demoResult?.competitor_mentions ?? []
      if (mentions.length === 0) {
        return { ...state, step: 'finishing', turns }
      }
      turns.push(assistantInteractive('Which of these are actually your competitors?', 'competitor_confirm'))
      return { ...state, step: 'confirming_competitors', turns }
    }

    case 'CONFIRM_COMPETITORS': {
      const turns = resolveLastInteractive(state.turns)
      const n = action.names.length
      turns.push(userTurn(n === 0 ? 'None of these are competitors.' : `Confirmed ${n} competitor${n === 1 ? '' : 's'}.`))
      return { ...state, step: 'finishing', turns, answers: { ...state.answers, confirmedCompetitors: action.names } }
    }

    case 'FINISHED': {
      return { ...state, step: 'done' }
    }

    default:
      return state
  }
}

export function createInitialMachineState(firstName: string, canCreateClients = false): OnboardingMachineState {
  return {
    firstName,
    canCreateClients,
    step: 'asking_website',
    websiteId: null,
    answers: {},
    turns: buildGreetingTurns(firstName, canCreateClients),
    error: null,
    promptsError: null,
  }
}

export function useOnboardingMachine(firstName: string, canCreateClients = false) {
  const [state, dispatch] = React.useReducer(
    onboardingReducer,
    { firstName, canCreateClients },
    ({ firstName: fn, canCreateClients: ccc }) => createInitialMachineState(fn, ccc)
  )
  return { state, dispatch }
}
